import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useDestination, type DestinationPerson, type DestinationStaffContext } from '@traceio-org/auth-ui';
import type { TraceEnvironment } from '@traceio-org/platform-contracts';
import type { AdminApi } from './api';
import { staffAbilities, type StaffAbilities } from './permissions';

export interface AdminContextValue {
  /** The staff reads, with admin-audience tokens from the confirmed session. */
  api: AdminApi;
  /** The account hub (sign-in, sign-out). */
  hubOrigin: string;
  /** platform-api base URL: relative brand asset paths (/brand-assets/...) resolve against it. */
  assetBaseUrl: string;
  /** The environment Trace Admin is built for (the registry confirmed it): release flags, development links. */
  environment: TraceEnvironment;
  /** Full-page navigation to the account hub (Account & billing, Sign out): the browser's, a test's stand-in. */
  assign: (url: string) => void;
}

const AdminContext = createContext<AdminContextValue | null>(null);

export function AdminProvider({ value, children }: { value: AdminContextValue; children: ReactNode }) {
  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export function useAdmin(): AdminContextValue {
  const value = useContext(AdminContext);
  if (!value) throw new Error('useAdmin() works only inside AdminProvider');
  return value;
}

export interface Staff {
  person: DestinationPerson;
  context: DestinationStaffContext;
}

/**
 * The staff identity DestinationGate confirmed through GET /v1/admin/me. Trace Admin's gate only ever
 * grants a staff context; anything else is a wiring bug and fails loudly instead of rendering.
 */
export function useStaff(): Staff {
  const access = useDestination();
  if (access.productKey !== 'admin' || access.context.type !== 'staff') {
    throw new Error('Trace Admin rendered without a confirmed staff context');
  }
  return { person: access.person, context: access.context };
}

/** What the signed-in staff member may try here; platform-api decides again on every request. */
export function useAbilities(): StaffAbilities {
  const { context } = useStaff();
  return useMemo(() => staffAbilities(context), [context]);
}
